import React, { useState, useMemo } from "react";
import { Table, Tag, Avatar, Button, Dropdown, message } from "antd";
import { DownOutlined, DownloadOutlined } from "@ant-design/icons";
import { translations } from "../../../utils/Translation/staticTranslation";
import { ConfigProvider } from "antd";
import { api } from "../../../services/axios";

const TopProductsTable = ({ merchantProductData }) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.MerchantAnalytics || {};
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [exportLoading, setExportLoading] = useState(false);

  // ✅ Transform API response to table-ready data
  const data = useMemo(() => {
    if (!merchantProductData?.result?.top_20) return [];

    return merchantProductData.result.top_20.map((item, index) => {
      const initials = item.store_name
        ? item.store_name.substring(0, 2).toUpperCase()
        : "NA";

      const colorPalette = [
        "#22C55E",
        "#FACC15",
        "#38BDF8",
        "#A855F7",
        "#EF4444",
        "#14B8A6",
        "#F97316",
      ];

      // Use Arabic names if language is Arabic
      const productName =
        lang === "ar"
          ? item.arabic_product_name || item.product_name
          : item.product_name;
      const storeName =
        lang === "ar"
          ? item.arabic_store_name || item.store_name
          : item.store_name;

      // Get Arabic category name from analytics if available
      const categoryDisplayName = item.category
        ? lang === "ar"
          ? merchantProductData?.result?.analytics?.[item.category]
              ?.category_arabic || item.category
          : item.category
        : item.category;

      return {
        key: index + 1,
        product: {
          name: productName,
          code: item.category?.toUpperCase(),
          category: item.category,
          img: item.image_url,
          arabicName: item.arabic_product_name,
          originalName: item.product_name,
        },
        store: {
          name: storeName,
          arabicName: item.arabic_store_name,
          originalName: item.store_name,
          initials: storeName
            ? storeName.substring(0, 2).toUpperCase()
            : initials,
          color: colorPalette[index % colorPalette.length],
        },
        category: item.category,
        categoryDisplayName: categoryDisplayName,
        views: item.views?.toLocaleString() ?? "0",
        impressions: item.clicks?.toLocaleString() ?? "0",
        ctr: item.ctr ?? "0%",
      };
    });
  }, [merchantProductData, lang]);

  // ✅ Export handler - calls API endpoint
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await api({
        method: "get",
        url: "/account/export_products/",
        responseType: "blob", // Important for file downloads
      });

      // Create blob from response
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "top20_products.csv";
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success(t.tables?.export_success || "Export successful");
    } catch (error) {
      console.error("Export error:", error);
      message.error(
        error.response?.data?.error ||
          t.tables?.export_error ||
          "Failed to export products"
      );
    } finally {
      setExportLoading(false);
    }
  };

  // 🧾 Export dropdown menu
  const exportMenu = {
    items: [
      {
        key: "csv",
        label: (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              color: "#101828",
            }}
          >
            <DownloadOutlined style={{ fontSize: 16 }} />
            {t.tables?.csv_file || "CSV File"}
          </div>
        ),
      },
    ],
    onClick: (e) => {
      if (e.key === "csv") handleExport();
    },
  };

  // 🧩 Table columns
  const columns = [
    {
      title: t.tables?.product || "Product",
      dataIndex: "product",
      key: "product",
      render: (product, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <img
            src={product.img}
            alt={product.name}
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              objectFit: "cover",
            }}
            onError={(e) => (e.target.style.display = "none")}
          />
          <div>
            <div style={{ fontWeight: 600 }}>{product.name}</div>
            <div style={{ color: "#6B7280", fontSize: 12 }}>
              {/* {product.code} • {product.category} */}
              {record.categoryDisplayName}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t.tables?.store || "Store",
      dataIndex: "store",
      key: "store",
      render: (store) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Avatar style={{ backgroundColor: store.color }}>
            {store.initials}
          </Avatar>
          <span style={{ fontWeight: 500 }}>{store.name}</span>
        </div>
      ),
    },
    {
      title: t.tables?.category || "Category",
      dataIndex: "category",
      key: "category",
      render: (text, record) => (
        <Tag
          style={{
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: "2px 10px",
            fontSize: 13,
            color: "#101828",
          }}
        >
          {record.categoryDisplayName || text}
        </Tag>
      ),
    },
    { title: t.tables?.views || "Views", dataIndex: "views", key: "views" },
    {
      title: t.tables?.clicks || "Clicks",
      dataIndex: "impressions",
      key: "impressions",
    },
    {
      title: "CTR",
      dataIndex: "ctr",
      key: "ctr",
      render: (ctr) => (
        <Tag
          style={{
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: "2px 10px",
            fontSize: 13,
            color: "#101828",
          }}
        >
          {ctr}
        </Tag>
      ),
    },
  ];

  return (
    <div style={{ background: "#F9FAFB", paddingTop: 20 }}>
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 600, color: "#000" }}>
          {t.tables?.top_20_products || "Top 20 Products"}
        </h2>

        <Dropdown menu={exportMenu} trigger={["click"]}>
          <Button
            type="primary"
            loading={exportLoading}
            style={{
              backgroundColor: "#3E3A36",
              border: "none",
              borderRadius: 8,
              height: 36,
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "0 24px",
            }}
          >
            {t.tables?.export || "Export"}
            <DownOutlined style={{ fontSize: 12, color: "#fff" }} />
          </Button>
        </Dropdown>
      </div>

      {/* Table Section */}
      <div
        style={{
          background: "white",
          borderRadius: 12,
          overflow: "hidden", // ✅ ensures smooth rounded edges
          borderTop: "1px solid #E5E7EB", // ✅ Added top border
          boxShadow: "0 1px 4px #E5E7EB",
        }}
      >
        <ConfigProvider direction={lang === "ar" ? "rtl" : "ltr"}>
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data.length,
              onChange: (page) =>
                setPagination({ ...pagination, current: page }),
              showSizeChanger: false,
            }}
            style={{
              borderRadius: 12,
            }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default TopProductsTable;
